import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

// Components
import FloatingButton from '../../components/iframe/FloatingButton';
import ChatForm from '../../components/iframe/ChatForm';
import ChatWindow from '../../components/iframe/ChatWindow';

// Hooks
import { useChatStream } from '../../hooks/iframe/useChatStream';
import { useChatState } from '../../hooks/iframe/useChatState';
import { useIframeCommunication } from '../../hooks/iframe/useIframeCommunication';

const AgentChatIframe = () => {
  const { id: agentId } = useParams();
  const [searchParams] = useSearchParams();

  // URL Parameters
  const apiKey = searchParams.get('apiKey');
  const position = searchParams.get('position') || 'bottom-right';
  const margin = searchParams.get('margin') || '20';
  
  // Default colors
  const defaultPrimaryColor = "#4309ffff";
  const defaultSecondaryColor = "#000000ff";

  // Custom hooks
  const { 
    agent, 
    loading, 
    error,
    isOpen, 
    messages, 
    setMessages, 
    chatId,
    chatState,
    setChatState,
    showForm, 
    collectedFormData,
    isLoadingPreviousChat,
    handleOpenChat,
    handleCloseChat,
    handleFormSubmit,
    handleNewChat,
    handleEndChat
  } = useChatState(agentId, apiKey);
  const { isMobile } = useIframeCommunication(agentId, isOpen, agent?.widget_settings);

  const handleChatStateChange = (newState) => {
    console.log("aqui")
    console.log(newState)
    setChatState(newState);
  };
  const { isTyping, sendMessage } = useChatStream(agentId, apiKey, chatId, collectedFormData, handleChatStateChange );

  // Utility functions
  const getContrastColor = (hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };
  
  const getPositionStyles = () => {
    const marginPx = `${margin}px`;
    const positions = {
      'bottom-right': { bottom: marginPx, right: marginPx },
      'bottom-left': { bottom: marginPx, left: marginPx },
      'top-right': { top: marginPx, right: marginPx },
      'top-left': { top: marginPx, left: marginPx }
    };
    return positions[position] || positions['bottom-right'];
  };

  // Apply custom CSS variables for colors
  useEffect(() => {
    if (!agent?.widget_settings) return;
    
    const settings = agent.widget_settings;
    const style = document.createElement('style');
    style.textContent = `
      :root {
        --agent-primary-color: ${settings.primary_color || defaultPrimaryColor};
        --agent-secondary-color: ${settings.secondary_color || defaultSecondaryColor};
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [agent]);

  // Event handlers
  const handleSendMessage = (messageText) => {
    sendMessage(messageText, setMessages);
  };

  // Loading and error states
  if (loading) {
    return <></>;
  }

  if (error) {
    return <div className="w-full h-full flex items-center justify-center text-red-500">Erro: {error}</div>;
  }

  if (!agent?.widget_settings) {
    return <></>;
  }

  const primaryColor = agent.widget_settings.primary_color || defaultPrimaryColor;

  // Render states
  if (!isOpen) {
    return (
      <FloatingButton 
        agent={agent}
        onClick={handleOpenChat}
        primaryColor={primaryColor}
        tooltipMessage={agent.widget_settings?.tooltipMessage || ''}
        tooltipPosition={agent.widget_settings?.tooltipPosition || 'top'}
        tooltipBackgroundColor={agent.widget_settings?.tooltipPosition || 'white'}
        tooltipTextColor={agent.widget_settings?.tooltipPosition || 'grey'}
      />
    );
  }

  if (showForm && agent.widget_settings.form?.active) {
    return (
      <ChatForm 
        agent={agent}
        onClose={handleCloseChat}
        onSubmit={handleFormSubmit}
        isMobile={isMobile}
        getPositionStyles={getPositionStyles}
      />
    );
  }

  return (
    <ChatWindow 
      agent={agent}
      messages={messages}
      isTyping={isTyping}
      isLoadingPreviousChat={isLoadingPreviousChat}
      chatState={chatState}
      onClose={handleCloseChat}
      onSendMessage={handleSendMessage}
      onNewChat={handleNewChat}
      isMobile={isMobile}
      getPositionStyles={getPositionStyles}
      getContrastColor={getContrastColor}
      onEndChat={handleEndChat}
      showForm={showForm}
      onStartNewChat={handleNewChat}
    />
  );
};

export default AgentChatIframe;